// Lightweight client-side search hook shared across the admin lists
// (Tagging Rules, File Workflows, Storage Containers). Captures the
// FR #184/185/186 pattern in one place so the three admins stay in
// sync as we tune fuzziness, normalization, and URL persistence.
//
// Pipeline contract: callers compose this hook into their existing
// filter chain. The recommended order is
//
//   all → nest-filter → search-filter → table-sort
//
// search-filter runs against the *current* (already nest-filtered)
// list so the row count in the page subtitle stays honest.
//
// Match semantics — v1:
//
// - Case-insensitive substring.
// - Whitespace-trimmed query.
// - Match if ANY of the configured fields contains the query.
// - `null` / `undefined` field values count as empty string (no
//   match) — operators don't have to guard against missing
//   description columns.
// - Each field is read via a per-call extractor so the same hook
//   works against discriminated union types without forcing a
//   common shape.
//
// Optional URL mirror — when `urlParam` is provided:
//
// - On first mount, seeds the query from `?<urlParam>=<value>` in
//   the current URL (read once; no live binding to popstate so an
//   in-page Back doesn't fight the operator's typing).
// - On every change, writes the query back via
//   `window.history.replaceState` so a copy-pasted URL preserves
//   the search.
//
// This is intentionally not a TanStack Query layer — search is a
// view-state concern. The list query stays unchanged and we filter
// the already-cached data, which keeps the UI snappy and avoids
// extra round trips.

import { useEffect, useMemo, useState } from 'react';

export interface UseListSearchOptions<T> {
  /**
   * Field extractors. Each function returns a stringy haystack for
   * one row; the hook stitches them with `\n` so substring matches
   * across fields don't accidentally match span boundaries (a
   * tab/newline can't appear inside an operator's typed query).
   */
  fields: ReadonlyArray<(item: T) => string | null | undefined>;

  /**
   * If set, mirror the query to `?<urlParam>=<value>` in the address
   * bar and seed initial state from the same. Skip when the host
   * page already manages its own ?q= for a different concern.
   */
  urlParam?: string;
}

export interface UseListSearchResult<T> {
  query: string;
  setQuery: (next: string) => void;
  /** The input list after the search predicate runs. */
  filtered: T[];
  /** True when the query is non-empty after trim. */
  isActive: boolean;
}

export function useListSearch<T>(
  items: ReadonlyArray<T> | undefined,
  { fields, urlParam }: UseListSearchOptions<T>,
): UseListSearchResult<T> {
  const [query, setQueryRaw] = useState<string>(() => {
    if (!urlParam || typeof window === 'undefined') return '';
    const v = new URLSearchParams(window.location.search).get(urlParam);
    return v ?? '';
  });

  const setQuery = (next: string) => {
    setQueryRaw(next);
    if (!urlParam || typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    if (next.trim()) {
      sp.set(urlParam, next);
    } else {
      sp.delete(urlParam);
    }
    const qs = sp.toString();
    const url = window.location.pathname + (qs ? `?${qs}` : '');
    window.history.replaceState(null, '', url);
  };

  // Effect: clear stale URL state if the consumer programmatically
  // resets state (e.g., switching nest filters can render the search
  // moot). Cheap; runs only on string equality.
  useEffect(() => {
    if (!urlParam || typeof window === 'undefined') return;
    const sp = new URLSearchParams(window.location.search);
    const current = sp.get(urlParam) ?? '';
    if (current === query) return;
    if (query.trim()) sp.set(urlParam, query);
    else sp.delete(urlParam);
    const qs = sp.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [query, urlParam]);

  const filtered = useMemo<T[]>(() => {
    const list = items ?? [];
    const needle = query.trim().toLowerCase();
    if (!needle) return [...list];
    return list.filter((item) => {
      const haystack = fields
        .map((f) => (f(item) ?? '').toString().toLowerCase())
        .join('\n');
      return haystack.includes(needle);
    });
    // `fields` is stable per-call (callers redefine each render); we
    // re-run on every input change anyway, so listing fields in deps
    // wouldn't help and risks stale-closure bugs if a caller passes
    // an inline arrow that captures state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query]);

  return {
    query,
    setQuery,
    filtered,
    isActive: query.trim().length > 0,
  };
}
